import React from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import {
  useAssumeRole,
  useInternalAccounts,
} from '../../data/useInternalAdmin';
import type { InternalAccount } from '../../data/useInternalAdmin';
import {
  ALLOWED_ASSUMED_ROLES,
  MAX_CONCURRENT_ASSUMED_ROLES,
} from '../../utils/internalPermissions';

interface AssumeRoleDialogProps {
  open: boolean;
  onClose: () => void;
  activeCount: number;
  /** Pre-select an account when opening from the account detail page */
  preselectedAccount?: InternalAccount | null;
}

const ROLE_LABELS: Record<string, string> = {
  viewer: 'Viewer',
  creator: 'Creator',
  'account-admin': 'Account Admin',
};

const AssumeRoleDialog: React.FC<AssumeRoleDialogProps> = ({
  open,
  onClose,
  activeCount,
  preselectedAccount,
}) => {
  const { user } = useAuth();
  const internalRole = user?.internal_role;
  const navigate = useNavigate();

  const [selectedAccount, setSelectedAccount] =
    React.useState<InternalAccount | null>(null);

  // Sync preselectedAccount when dialog opens
  React.useEffect(() => {
    if (open && preselectedAccount) {
      setSelectedAccount(preselectedAccount);
    }
  }, [open, preselectedAccount]);
  const [role, setRole] = React.useState('viewer');
  const [reason, setReason] = React.useState('');
  const [durationHours, setDurationHours] = React.useState(1);
  const [error, setError] = React.useState<string | null>(null);

  const accountsQuery = useInternalAccounts();
  const assumeRole = useAssumeRole();
  const accounts = accountsQuery.data?.data || [];

  const allowedRoles = ALLOWED_ASSUMED_ROLES[internalRole || ''] || [];

  const handleSubmit = () => {
    if (!selectedAccount) return;
    const accountName = selectedAccount.attributes.name;
    setError(null);
    assumeRole.mutate(
      {
        account_id: selectedAccount.id,
        assumed_role: role,
        reason,
        duration_hours: durationHours,
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
          navigate(`/${accountName}/flows`);
        },
        onError: (err: any) => {
          const detail = err?.response?.data?.detail || 'Failed to assume role';
          setError(detail);
        },
      },
    );
  };

  const resetForm = () => {
    setSelectedAccount(null);
    setRole('viewer');
    setReason('');
    setDurationHours(1);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assume Role</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {activeCount >= MAX_CONCURRENT_ASSUMED_ROLES && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You have {activeCount} active assumed roles. You may need to end one
            before assuming another.
          </Alert>
        )}
        <Autocomplete
          options={accounts}
          getOptionLabel={(opt) =>
            opt.attributes.title
              ? `${opt.attributes.name} (${opt.attributes.title})`
              : opt.attributes.name
          }
          value={selectedAccount}
          onChange={(_, value) => setSelectedAccount(value)}
          loading={accountsQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Account"
              size="small"
              sx={{ mt: 1, mb: 2 }}
            />
          )}
        />
        <TextField
          select
          label="Role"
          fullWidth
          size="small"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          sx={{ mb: 2 }}
        >
          {allowedRoles.map((r) => (
            <MenuItem key={r} value={r}>
              {ROLE_LABELS[r] || r}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Reason"
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Required — explain why you need this access"
          sx={{ mb: 2 }}
        />
        <TextField
          select
          label="Duration (hours)"
          fullWidth
          size="small"
          value={durationHours}
          onChange={(e) => setDurationHours(Number(e.target.value))}
        >
          <MenuItem value={1}>1 hour</MenuItem>
          <MenuItem value={2}>2 hours</MenuItem>
          <MenuItem value={3}>3 hours</MenuItem>
          <MenuItem value={4}>4 hours</MenuItem>
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedAccount || !reason.trim() || assumeRole.isPending}
        >
          {assumeRole.isPending ? 'Assuming...' : 'Assume Role'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssumeRoleDialog;
